import { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Stack,
    Typography,
} from '@mui/material';
import {
    ListBase,
    Pagination,
    useListContext,
    useShowContext,
} from 'react-admin';

import { type Receipt } from '@/modules/receipts/receiptApi';
import { ReceiptPreviewDialog } from '@/modules/receipts/ReceiptPreviewDialog';
import { formatDate } from '@/utils/date_utils';
import { QuoteJobFolder } from '@/types';

export const ProjectReceiptsTab = () => {
    const { record } = useShowContext<QuoteJobFolder>();

    if (!record) return null;

    return (
        <ListBase
            disableAuthentication
            resource="receipts"
            filter={{ project_id: record.id, deleted_at: null }}
            sort={{ field: 'id', order: 'DESC' }}
            perPage={25}
        >
            <ProjectReceiptsLayout />
        </ListBase>
    );
};

const ProjectReceiptsLayout = () => {
    const { data, isPending } = useListContext<Receipt>();

    return (
        <Stack sx={{ width: '100%', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ px: 1 }}>
                Receipts
            </Typography>
            {!isPending && !data?.length ? (
                <Alert severity="info">No receipts found for this project.</Alert>
            ) : (
                <>
                    <Card>
                        <ProjectReceiptsContent />
                    </Card>
                    <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
                </>
            )}
        </Stack>
    );
};

const ProjectReceiptsContent = () => {
    const { data: receipts = [], isPending, error } = useListContext<Receipt>();
    const [preview, setPreview] = useState<Receipt | null>(null);

    if (isPending) return null;
    if (error) {
        return <Alert severity="error">{error.message}</Alert>;
    }

    return (
        <>
            <Stack spacing={0}>
                {receipts.map(receipt => (
                    <CardContent
                        key={receipt.id}
                        sx={{
                            py: 1.5,
                            '&:last-child': { pb: 1.5 },
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            spacing={1}
                        >
                            <Box>
                                <Typography>
                                    {receipt.merchant || receipt.original_filename}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Uploaded{' '}
                                    {formatDate(
                                        receipt.created_at,
                                        'MM-dd-yyyy HH:mm'
                                    )}
                                    {' · '}
                                    {receipt.transaction_date || 'No purchase date'}
                                    {' · '}
                                    {receipt.allocation_type === 'project_cost'
                                        ? 'Project cost (COGS)'
                                        : receipt.category}
                                    {receipt.subcategory
                                        ? `: ${receipt.subcategory}`
                                        : ''}
                                    {receipt.project_no != null
                                        ? ` · Project #${receipt.project_no}`
                                        : ''}
                                </Typography>
                                {receipt.allocation_status === 'needs_correction' && (
                                    <Typography
                                        variant="body2"
                                        color="error.main"
                                        mt={0.5}
                                    >
                                        Bookkeeper correction requested:{' '}
                                        {receipt.allocation_review_notes}
                                    </Typography>
                                )}
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {receipt.amount && (
                                    <Typography>${receipt.amount}</Typography>
                                )}
                                <Button
                                    size="small"
                                    onClick={() => setPreview(receipt)}
                                >
                                    View
                                </Button>
                            </Stack>
                        </Stack>
                    </CardContent>
                ))}
            </Stack>
            <ReceiptPreviewDialog
                receipt={preview}
                onClose={() => setPreview(null)}
            />
        </>
    );
};
